<div class="container-fixed">
  <!-- Header -->
  <div class="flex flex-wrap items-center lg:items-end justify-between gap-5 pb-7.5">
    <div class="flex flex-col justify-center gap-2">
      <h1 class="text-xl font-semibold leading-none text-gray-900">
        Staff Schedule Management
      </h1>
      <div class="flex items-center gap-2 text-sm font-medium text-gray-600">
        Configure weekly work schedules for staff members
      </div>
    </div>
    <div class="flex items-center gap-2.5">
      <!-- Add New Schedule Button -->
      <button class="btn btn-sm btn-primary" (click)="openNewScheduleModal()" [disabled]="getAvailableStaff().length === 0">
        <i class="ki-outline ki-plus"></i>
        Add Schedule
      </button>
      <!-- Refresh Button -->
      <button class="btn btn-sm btn-light" (click)="loadSchedules()">
        <i class="ki-outline ki-arrows-circle"></i>
        Refresh
      </button>
    </div>
  </div>

  <!-- Main Content -->
  <div class="card">
    <div class="card-header">
      <h3 class="card-title">Weekly Schedules</h3>
    </div>
    <div class="card-body">
      <!-- Loading State -->
      <div *ngIf="isLoading" class="flex justify-center py-10">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>

      <!-- No Data State -->
      <div *ngIf="!isLoading && staffList.length === 0" class="text-center py-10 text-gray-500">
        <i class="ki-outline ki-calendar text-4xl mb-3"></i>
        <p>No staff schedules configured yet.</p>
        <p class="text-sm" *ngIf="staffMembers.length === 0">Use the system user management to add staff members first.</p>
        <button *ngIf="staffMembers.length > 0" class="btn btn-sm btn-primary mt-4" (click)="openNewScheduleModal()">
          <i class="ki-outline ki-plus me-1"></i>
          Add First Schedule
        </button>
      </div>

      <!-- Schedule Table -->
      <div *ngIf="!isLoading && staffList.length > 0" class="scrollable-x-auto">
        <table class="table table-auto table-border">
          <thead>
            <tr>
              <th class="min-w-[180px]">Staff Member</th>
              <th *ngFor="let day of daysOfWeek" class="min-w-[100px] text-center">
                {{ day.shortLabel }}
              </th>
              <th class="min-w-[80px] text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let staff of staffList">
              <!-- Staff Name -->
              <td>
                <div class="flex items-center gap-2.5">
                  <div class="flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-gray-700 font-semibold">
                    {{ staff.firstName?.charAt(0) }}{{ staff.lastName?.charAt(0) }}
                  </div>
                  <div class="flex flex-col">
                    <span class="text-sm font-medium text-gray-900">{{ staff.staffName }}</span>
                    <span class="text-xs text-gray-500">{{ getScheduledDaysCount(staff) }} days/week</span>
                  </div>
                </div>
              </td>

              <!-- Schedule for each day -->
              <td *ngFor="let day of daysOfWeek" class="text-center">
                <div *ngIf="hasScheduleForDay(staff, day.value)" class="schedule-cell active">
                  <span class="text-xs">{{ getScheduleDisplay(staff, day.value) }}</span>
                </div>
                <div *ngIf="!hasScheduleForDay(staff, day.value)" class="schedule-cell inactive">
                  <span class="text-gray-400">-</span>
                </div>
              </td>

              <!-- Actions -->
              <td class="text-center">
                <div class="flex justify-center gap-1">
                  <button
                    class="btn btn-sm btn-icon btn-light"
                    (click)="openScheduleModal(staff)"
                    title="Edit Schedule">
                    <i class="ki-outline ki-notepad-edit"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-icon btn-light text-danger"
                    (click)="deleteAllSchedules(staff)"
                    title="Delete All Schedules">
                    <i class="ki-outline ki-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</div>

<!-- Edit Schedule Modal -->
<div class="modal-backdrop" *ngIf="showScheduleModal" (click)="closeScheduleModal()"></div>
<div class="modal show" *ngIf="showScheduleModal">
  <div class="modal-content">
    <div class="modal-header">
      <h3 class="modal-title">
        {{ selectedStaff ? 'Edit Schedule - ' + selectedStaff.staffName : 'New Schedule' }}
      </h3>
      <button class="btn btn-xs btn-icon btn-light" (click)="closeScheduleModal()">
        <i class="ki-outline ki-cross"></i>
      </button>
    </div>
    <div class="modal-body">
      <form [formGroup]="scheduleForm">
        <!-- Staff Selector (for new schedules only) -->
        <div class="mb-4" *ngIf="!selectedStaff">
          <label class="form-label text-gray-900">Select Staff Member <span class="text-danger">*</span></label>
          <select class="select select-sm w-full" formControlName="userID">
            <option [ngValue]="null" disabled>-- Select Staff --</option>
            <option *ngFor="let staff of getAvailableStaff()" [ngValue]="staff.userID">
              {{ staff.firstName }} {{ staff.lastName }}
            </option>
          </select>
        </div>

        <!-- Quick Actions -->
        <div class="mb-4 flex gap-2">
          <button type="button" class="btn btn-xs btn-light" (click)="applyToWeekdays()">
            Apply Monday to Weekdays
          </button>
        </div>

        <!-- Schedule Grid -->
        <div formArrayName="schedules" class="space-y-3">
          <div *ngFor="let dayControl of schedulesFormArray.controls; let i = index"
               [formGroupName]="i"
               class="flex items-center gap-3 p-3 rounded-lg"
               [class.bg-success-light]="dayControl.get('isActive')?.value"
               [class.bg-gray-50]="!dayControl.get('isActive')?.value">

            <!-- Enable Toggle -->
            <div class="w-24">
              <label class="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" class="checkbox checkbox-sm" formControlName="isActive">
                <span class="text-sm font-medium">{{ daysOfWeek[i].label }}</span>
              </label>
            </div>

            <!-- Time Inputs -->
            <div class="flex items-center gap-2" *ngIf="dayControl.get('isActive')?.value">
              <input type="time" class="input input-sm w-28" formControlName="startTime">
              <span class="text-gray-500">to</span>
              <input type="time" class="input input-sm w-28" formControlName="endTime">
            </div>

            <!-- Disabled state -->
            <div *ngIf="!dayControl.get('isActive')?.value" class="text-gray-400 text-sm">
              Off
            </div>
          </div>
        </div>
      </form>
    </div>
    <div class="modal-footer justify-end">
      <button class="btn btn-light btn-sm" (click)="closeScheduleModal()">Cancel</button>
      <button class="btn btn-primary btn-sm" (click)="saveSchedule()" [disabled]="isLoading">
        <span *ngIf="isLoading" class="spinner-border spinner-border-sm me-2"></span>
        Save Schedule
      </button>
    </div>
  </div>
</div>
